import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DataTable from 'components/shared/DataTable';
import TablePagination from 'components/shared/Pagination/TablePagination';
import { usePagination } from 'utils/hooks/usePagination';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import axiosPrivate from 'config/axiosPrivate';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import './AISmartcontent.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_REDACTION_IA_BRIEF_GENERATOR_PAGE = '/AdminClient/BriefGenerator';
const BRIEFS_PER_PAGE = 10;

const normalizeUrl = (input) =>
  (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const normalizeBriefList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.texts)) return payload.texts;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getBriefContentTypeLabel = (t, raw) => {
  if (raw == null || raw === '') return '-';
  const key = String(raw).trim().toUpperCase();
  const labelMap = {
    OTHER: t('Contenu web'),
    PAGE_CONTENU: t('Contenu web'),
    LONG_CATEGORY: t('Page catégorie'),
    STORE_LOCATOR: t('Store locator'),
    FAQ: t('FAQ'),
    GMB: t('Fiche GMB'),
  };
  return labelMap[key] || String(raw);
};

export default function BriefGenerator() {
  usePageTracking(ADMIN_CLIENT_REDACTION_IA_BRIEF_GENERATOR_PAGE);

  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [briefs, setBriefs] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [briefPendingDelete, setBriefPendingDelete] = useState(null);

  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients, isLoadingClients = false } = useSelector(adminClientClientListSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];
  const isClientsReady = clients !== null && !isLoadingClients;
  const hasNoWebsiteConfigured = isClientsReady && clientsList.length === 0;

  const currentWebsiteId = useMemo(() => {
    if (!selectedWebsiteUrl || clientsList.length === 0) return null;
    const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
    const selected = clientsList.find((website) => {
      const wUrl = website?.urlSite || website?.url || '';
      const wName = website?.name || '';
      return (
        wUrl === selectedWebsiteUrl ||
        wName === selectedWebsiteUrl ||
        normalizeUrl(wUrl) === normalizedSaved ||
        normalizeUrl(wName) === normalizedSaved
      );
    });
    return selected?.id || null;
  }, [clientsList, selectedWebsiteUrl]);

  useEffect(() => {
    document.title = 'Typix - Brief generator';
  }, []);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(() => {
    const fetchBriefs = async () => {
      if (clients === null) {
        return;
      }

      if (!selectedWebsiteUrl || !currentWebsiteId) {
        setBriefs([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setBriefs([]);
      try {
        const { data } = await axiosPrivate.get(
          `/texts/external/unassigned/by-website/${currentWebsiteId}/having-writing-focus`
        );
        setBriefs(normalizeBriefList(data));
      } catch (error) {
        console.error('Erreur lors de la récupération des briefs:', error);
        setBriefs([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBriefs();
  }, [clients, selectedWebsiteUrl, currentWebsiteId]);

  const handleCreate = () => {
    try {
      localStorage.removeItem('AdminClient_TextId');
      sessionStorage.removeItem('AdminClient_BriefTextId');
      sessionStorage.removeItem('AdminClient_FromBriefFlow');
      sessionStorage.removeItem('AdminClient_ReturningToBrief');
    } catch {
      // ignore storage errors
    }
    history.push('/AdminClient/createIAbrief');
  };

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  const handleOpenBrief = useCallback(
    (row) => {
      if (row?.id == null || row.id === '') return;
      const idStr = String(row.id);
      try {
        localStorage.setItem('AdminClient_TextId', idStr);
        sessionStorage.setItem('AdminClient_BriefTextId', idStr);
      } catch {
        // ignore storage errors
      }
      history.push(`/AdminClient/createIAbrief/${idStr}`);
    },
    [history]
  );

  const requestDeleteBrief = useCallback((row) => {
    if (row?.id == null || row.id === '') return;
    setBriefPendingDelete(row);
    setConfirmOpen(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setConfirmOpen(false);
    setBriefPendingDelete(null);
  }, []);

  const executeDeleteBrief = useCallback(async () => {
    const id = briefPendingDelete?.id;
    if (!id) {
      closeDeleteConfirm();
      return;
    }
    try {
      await axiosPrivate.patch(`/texts/external/unassigned/${id}/clear-writing-focus`);
      setBriefs((prev) => (Array.isArray(prev) ? prev.filter((brief) => brief?.id !== id) : prev));
    } catch (error) {
      console.error('Erreur lors de la suppression du brief:', error);
    } finally {
      closeDeleteConfirm();
    }
  }, [briefPendingDelete, closeDeleteConfirm]);

  const columns = useMemo(
    () => [
      {
        header: t('Mot clé'),
        accessor: 'keyword',
        sortable: true,
        width: '75%',
        renderCell: (row) => (
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenBrief(row);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenBrief(row);
              }
            }}
            style={{
              color: 'black',
              cursor: row?.id ? 'pointer' : 'default',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {row.keyword}
          </span>
        ),
      },
      {
        header: t('Type de contenu'),
        accessor: 'contentTypeLabel',
        sortable: true,
        width: '20%',
        renderCell: (row) => (
          <span style={{ fontSize: 14, color: '#374151' }}>{row.contentTypeLabel}</span>
        ),
      },
      {
        header: t('Action'),
        accessor: 'actions',
        sortable: false,
        width: '5%',
        renderCell: (row) =>
          row?.id != null && row.id !== '' ? (
            <button
              type="button"
              aria-label={t('Supprimer')}
              onClick={(e) => {
                e.stopPropagation();
                requestDeleteBrief(row);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 4,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src="/Images/icon-trash.png"
                alt={t('Supprimer')}
                style={{ width: 22, height: 22 }}
              />
            </button>
          ) : null,
      },
    ],
    [t, handleOpenBrief, requestDeleteBrief]
  );

  const allRows = useMemo(
    () =>
      briefs.map((brief) => {
        const rawContentType =
          brief?.contentType ?? brief?.pageType ?? brief?.typeContenu ?? null;
        return {
          id: brief?.id,
          keyword: brief?.keyword || brief?.title || '-',
          contentType: rawContentType,
          contentTypeLabel: getBriefContentTypeLabel(t, rawContentType),
        };
      }),
    [briefs, t]
  );

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return allRows;
    return allRows.filter(
      (row) =>
        String(row.keyword || '').toLowerCase().includes(query) ||
        String(row.contentTypeLabel || '').toLowerCase().includes(query)
    );
  }, [allRows, searchValue]);

  const { currentPage, setCurrentPage, totalPages, paginatedItems } = usePagination(
    filteredRows,
    BRIEFS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, setCurrentPage]);

  const rows = paginatedItems;

  const emptyMessage = isLoading
    ? t('Chargement...')
    : searchValue.trim()
      ? t('Aucun brief trouvé.')
      : hasNoWebsiteConfigured
        ? t('Aucun contenu disponible — veuillez d’abord ajouter un siteweb.')
        : t('Aucun brief créé pour le moment.');

  return (
    <div style={{ marginTop: '-5px' }}>
      <div className="box-info-header">
        <div className="b-child-1">
          <h1>{t('Brief generator')}</h1>
        </div>
        <div className="b-child-2 d-flex justify-content-end align-items-center">
          <button
            type="button"
            onClick={handleCreate}
            disabled={hasNoWebsiteConfigured}
            aria-disabled={hasNoWebsiteConfigured}
            style={{
              padding: '6px 20px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#6458F1',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 400,
              cursor: hasNoWebsiteConfigured ? 'not-allowed' : 'pointer',
              opacity: hasNoWebsiteConfigured ? 0.55 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Poppins, sans-serif',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              if (e.currentTarget.disabled) return;
              e.currentTarget.style.backgroundColor = '#5548d1';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.disabled) return;
              e.currentTarget.style.backgroundColor = '#6458F1';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            + {t('Générer un nouveau brief')}
          </button>
        </div>
      </div>
      <hr
        style={{
          border: 'none',
          borderBottom: '1px solid rgb(179, 180, 182)',
          marginLeft: '-30px',
          marginBottom: 8,
          width: 'calc(100% + 40px)',
        }}
      />

      {hasNoWebsiteConfigured && (
        <div className="ai-smart-help-banner" role="note" style={{ marginBottom: 12 }}>
          <div className="ai-smart-help-banner-left">
            <span className="ai-smart-help-banner-text">
              {t('Aucun contenu disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          <div className="ai-smart-help-banner-actions">
            <button
              type="button"
              onClick={handleGoToAddWebsite}
              className="ai-smart-help-banner-link"
            >
              {t('Ajouter un siteweb')}
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {!isSearchOpen ? (
          <img
            src="/Images/search-icon.png"
            alt="search"
            onClick={() => setIsSearchOpen(true)}
            style={{
              width: '32px',
              height: '32px',
              cursor: 'pointer',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              maxWidth: '320px',
            }}
          >
            <div
              style={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img
                src="/Images/search-icon2.png"
                alt="search"
                style={{
                  position: 'absolute',
                  left: '10px',
                  width: '16px',
                  height: '16px',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('Rechercher un brief...')}
                style={{
                  width: '100%',
                  padding: '8px 36px 8px 34px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
                setSearchValue('');
              }}
              style={{
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#111827',
              }}
            >
              {t('Fermer')}
            </button>
          </div>
        )}
      </div>

      <DataTable
        headerClassName="ai-smart-table-head"
        columns={columns}
        data={rows}
        enableSorting
        onRowClick={handleOpenBrief}
        emptyStateContent={
          !isLoading && filteredRows.length === 0 ? (
            <div
              style={{
                padding: '10px 10px',
                textAlign: 'center',
                color: '#6B7280',
                fontSize: 14,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {emptyMessage}
            </div>
          ) : isLoading ? (
            <div
              style={{
                padding: '10px 10px',
                textAlign: 'center',
                color: '#6B7280',
                fontSize: 14,
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {t('Chargement...')}
            </div>
          ) : null
        }
      />

      <TablePagination
        currentPage={currentPage}
        totalPages={Math.max(1, totalPages)}
        onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
        onNext={() =>
          setCurrentPage((prev) => Math.min(Math.max(1, totalPages), prev + 1))
        }
        disablePrev={currentPage === 1}
        disableNext={
          isLoading ||
          filteredRows.length === 0 ||
          currentPage >= Math.max(1, totalPages)
        }
      />

      <ConfirmationModal
        open={confirmOpen}
        handleClose={closeDeleteConfirm}
        executeAction={executeDeleteBrief}
        message={t('Êtes-vous sûr de vouloir supprimer ce brief ?')}
      />
    </div>
  );
}
